import React, { useState, useEffect, useMemo } from 'react';
import { useBooking } from '../../hooks/useBooking';
import { formatLocationAddressLines, locationEmbedQuery } from '../../lib/colorUtils';
import { useSiteColors } from '../../hooks/useSiteColors';

export function LocationSection() {
  const { businessListing, locations } = useBooking();
  const c = useSiteColors();

  const [activeLocationIndex, setActiveLocationIndex] = useState(0);

  const textPrimary = c.text;
  const textSecondary = c.mutedText;
  const textMuted = c.mutedText;
  const cardBg = c.background;
  const borderColor = c.border;

  useEffect(() => {
    if (locations.length > 0 && activeLocationIndex >= locations.length) {
      setActiveLocationIndex(0);
    }
  }, [locations.length, activeLocationIndex]);

  const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

  const mapEmbedSrc = useMemo(() => {
    if (!mapsApiKey) return null;
    if (locations.length > 0) {
      const loc = locations[Math.min(activeLocationIndex, locations.length - 1)];
      const q = locationEmbedQuery(loc);
      if (q) {
        return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsApiKey)}&q=${encodeURIComponent(q)}`;
      }
      return null;
    }
    if (businessListing?.google_place_id) {
      return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsApiKey)}&q=${encodeURIComponent(`place_id:${businessListing.google_place_id}`)}`;
    }
    if (businessListing?.address) {
      return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(mapsApiKey)}&q=${encodeURIComponent(businessListing.address)}`;
    }
    return null;
  }, [mapsApiKey, locations, activeLocationIndex, businessListing?.google_place_id, businessListing?.address]);

  const hasLocationData =
    locations.length > 0 ||
    Boolean(businessListing?.address?.trim()) ||
    Boolean(businessListing?.google_place_id?.trim());

  if (!businessListing || !hasLocationData) return null;

  const selectedLocation =
    locations.length > 0
      ? locations[Math.min(activeLocationIndex, locations.length - 1)]
      : null;

  return (
    <div id="locations" className="flex flex-col gap-4 min-h-0">
      {locations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {locations.map((loc, i) => (
            <button
              key={loc.id}
              type="button"
              onClick={() => setActiveLocationIndex(i)}
              className="px-4 py-1.5 rounded-full text-sm font-medium transition-all max-w-full truncate"
              title={loc.name}
              style={
                activeLocationIndex === i
                  ? { backgroundColor: c.primaryFill, color: c.onPrimary, border: `1px solid ${c.primaryOutline ?? c.primaryFill}` }
                  : { backgroundColor: cardBg, color: textSecondary, border: `1px solid ${borderColor}` }
              }
            >
              {loc.name || formatLocationAddressLines(loc)[0] || `Location ${i + 1}`}
            </button>
          ))}
        </div>
      )}

      {mapEmbedSrc ? (
        <iframe
          title="Business location"
          key={selectedLocation?.id ?? 'default-map'}
          className="rounded-2xl w-full min-h-[400px]"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          src={mapEmbedSrc}
        />
      ) : (
        <div
          className="rounded-2xl overflow-hidden flex items-center justify-center min-h-[400px]"
          style={{ backgroundColor: cardBg, border: `1px dashed ${borderColor}` }}
        >
          <div className="text-center px-4" style={{ color: textMuted }}>
            <div className="text-5xl mb-3 opacity-40" aria-hidden>📍</div>
            <p className="text-sm">
              {mapsApiKey ? 'Add a place or address to show the map.' : 'Map API key not configured.'}
            </p>
          </div>
        </div>
      )}

      {selectedLocation ? (
        <div className="rounded-2xl p-6" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: c.link }}>
            {selectedLocation.name || 'Address'}
          </p>
          <div className="space-y-0.5">
            {formatLocationAddressLines(selectedLocation).map((line, i) => (
              <p key={i} className="text-[15px] font-medium leading-relaxed" style={{ color: textPrimary }}>
                {line}
              </p>
            ))}
          </div>
          {selectedLocation.arrival_instructions?.trim() ? (
            <p className="text-sm mt-4 leading-relaxed" style={{ color: textSecondary }}>
              {selectedLocation.arrival_instructions.trim()}
            </p>
          ) : null}
        </div>
      ) : null}

      {!selectedLocation && locations.length === 0 && (businessListing.address || businessListing.google_place_id) ? (
        <div className="rounded-2xl p-6" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: c.link }}>
            Address
          </p>
          {businessListing.address ? (
            <p className="text-[15px] font-medium" style={{ color: textPrimary }}>
              {businessListing.address}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
